import { L3_PATHS, staticRoutes } from 'Deer/constants/routes';
import { getUserAccess, getUserL2Access } from 'Deer/helpers/getUserAccess';
import {
  checkCrossModularInsightsEnabled,
  checkEnableCompanySupport,
  checkLifecycleInsightsEnabled,
  checkSettingsPageRevampEnabled,
  checkVisierAnalyticsEnabled,
  checkVisierReportsEnabled,
} from 'utils/helpers/featureFlags';

import { L2NavMenu, NavMenuItemsProps } from '../../L2NavMenu/L2NavMenu';
import { CompanyModules } from './CompanyModules';

const settingsMenuTitle = 'SETTINGS';

const settingsMenuItems: NavMenuItemsProps = [
  {
    label: 'Organisation Details',
    value: staticRoutes.company.settings.platform.organization,
    key: staticRoutes.company.settings.platform.organization,
  },
  {
    label: 'Employee Details',
    value: staticRoutes.company.settings.platform.employees,
    key: staticRoutes.company.settings.platform.employees,
  },
  {
    label: 'Competencies',
    value: staticRoutes.company.settings.competencies,
    key: staticRoutes.company.settings.competencies,
  },
  {
    label: 'Notifications',
    value: staticRoutes.company.settings.platform.notifications,
    key: staticRoutes.company.settings.platform.notifications,
  },
  {
    label: 'Function & Grade',
    value: staticRoutes.company.settings.platform['function-grade'],
    key: staticRoutes.company.settings.platform['function-grade'],
  },
  {
    label: 'Access Control',
    value: staticRoutes.company.settings.platform.access,
    key: staticRoutes.company.settings.platform.access,
  },
  {
    label: 'Module Preferences',
    value: staticRoutes.company.settings.platform.modules,
    key: staticRoutes.company.settings.platform.modules,
  },
  {
    label: 'Slack',
    value: staticRoutes.company.settings.plugins.slack,
    key: staticRoutes.company.settings.plugins.slack,
  },
  {
    label: 'MS Teams',
    value: staticRoutes.company.settings.plugins.msteams,
    key: staticRoutes.company.settings.plugins.msteams,
  },
  {
    label: 'HRIS',
    value: staticRoutes.company.settings.plugins.hris,
    key: staticRoutes.company.settings.plugins.hris,
  },
  {
    label: 'Platform Settings',
    value: [
      staticRoutes.company.settings.platform.organization,
      staticRoutes.company.settings.platform['function-grade'],
    ],
    key: 'PlatformSettings',
  },
  {
    label: 'Employee roster',
    value: [
      staticRoutes.company.settings.platform.employees,
      staticRoutes.company.settings.plugins.hris,
      staticRoutes.company.settings.platform.access,
    ],
    key: 'EmployeeRosterPages',
  },
  {
    label: 'Third party integration',
    value: [staticRoutes.company.settings.plugins.slack, staticRoutes.company.settings.plugins.msteams],
    key: 'ThirdPartyIntegrations',
  },
];

const newSettingMenuTitle = 'COMPANY SETTINGS';

const analyticsMenuTitle = 'INSIGHTS';
const analyticsMenuItems: NavMenuItemsProps = [
  {
    label: 'Data exports',
    value: staticRoutes.company.analytics.reports,
    key: 'analytics-reports',
    hide: () => !(checkVisierAnalyticsEnabled() || checkVisierReportsEnabled()),
  },
  {
    label: 'Outcomes Dashboard',
    value: staticRoutes.company.analytics.outcomesDashboard,
    key: 'outcomes-dashboard',
    hide: () => !(checkVisierAnalyticsEnabled() && checkCrossModularInsightsEnabled()),
    isBeta: true,
  },
  {
    label: 'Lifecycle insights',
    value: [
      L3_PATHS.company.lifecycle.hire,
      L3_PATHS.company.lifecycle.retire,
      L3_PATHS.company.lifecycle.onboard,
      L3_PATHS.company.lifecycle.progress,
    ],
    key: 'lifecycle-insights',
    hide: () => !checkLifecycleInsightsEnabled(),
    isBeta: true,
  },
];

export const CompanySecondaryNav = () => {
  const { isPlatformAdmin, isModuleAdmin } = getUserAccess();
  const { hasFeedbackAccess, hasIDPAccess } = getUserL2Access();
  const isSettingsPageRevampEnabled: boolean = checkSettingsPageRevampEnabled();

  let filteredSettingsMenuItems =
    hasFeedbackAccess || hasIDPAccess
      ? settingsMenuItems?.filter((item) => item.label !== 'Competencies')
      : settingsMenuItems;

  if (checkSettingsPageRevampEnabled()) {
    filteredSettingsMenuItems = filteredSettingsMenuItems.filter(
      (item) =>
        ![
          'Slack',
          'MS Teams',
          'HRIS',
          'Employee Details',
          'Organisation Details',
          'Function & Grade',
          'Access Control',
        ].includes(item.label)
    );
  } else {
    filteredSettingsMenuItems = filteredSettingsMenuItems.filter(
      (item) => !['Employee roster', 'Third party integration', 'Platform Settings'].includes(item.label)
    );
  }

  // FF based addition of a nav item to open a link on a new tab for demo
  if (checkEnableCompanySupport()) {
    filteredSettingsMenuItems.push({
      label: 'Admin setting',
      value: L3_PATHS.company.overview.myCompanyOverview,
      key: 'adminSettingDemo',
    });
  }

  return (
    <>
      <CompanyModules />
      {(checkVisierAnalyticsEnabled() || checkVisierReportsEnabled()) && isModuleAdmin && (
        <L2NavMenu menuTitle={analyticsMenuTitle} menuItems={analyticsMenuItems} />
      )}
      {isPlatformAdmin && (
        <L2NavMenu
          menuTitle={isSettingsPageRevampEnabled ? newSettingMenuTitle : settingsMenuTitle}
          menuItems={filteredSettingsMenuItems}
        />
      )}
    </>
  );
};
